import {EventEmitter, Injectable} from '@angular/core';
import {ApiClientService} from '../../../services/api-client.service';
import {AccountPermissionsService} from '../../../services/account-permissions.service';
import {IResidentPatch} from '../../../types/api-response/building/resident-patch';
import {IUser} from '../../../types/api-response/building/user';
import {IEmergencyContacts} from '../../../types/api-response/building/emergency-contacts';
import {map} from 'rxjs/operators';

declare const toastr: any;
declare const swal: any;
declare const $: any;

type UserAvatar = IUser & { avatar: string }; // Todo: fix and remove hack

@Injectable()
export class ProfileService {
    spinnerShow: boolean = false;
    disabledSubmit: boolean = false;
    isPasswordFormActive: boolean = false;
    showChangeWaiverSettingsModal: boolean = false;
    data: any = {};
    email: string = '';
    onConsentUpdated: EventEmitter<any> = new EventEmitter();
    petTypes = [];
    currentCorporationSettings = {};

    constructor(public api: ApiClientService,
                public account: AccountPermissionsService) {
        this.data = this.getLocalProfileData();
    }

    public get() {
        this.email = this.getLocalProfileData().email;
        const url = `/api/users/${this.email}/get.json?expand=company.theme,phones,addresses`;
        return this.api.get('oauthUrl', url).map(
            (res) => {
                this.data = JSON.parse(res._body);
                const localUserData = this.getLocalProfileData();
                localUserData.fullName = this.data.fullName;
                localUserData.avatar = this.data.avatar;
                localUserData.email = this.data.email;
                this.account.saveUser(JSON.stringify(localUserData));
                return this.data;
            },
            (error) => {
                this.api.errorHandler(error, 'Get profile failed!');
            }
        );
    }

    public update(data: Object) {
        let url = '';
        if (this.data.userType === 'resident') {
            const residentId = JSON.parse(localStorage.getItem('residentselectedBuilding')).residentBdId;
            url = `/v1/residents_self_update/${residentId}.json?expand=consent`;
            this.spinnerShow = true;
        } else
            url = `/api/v1/users/${this.data.id}.json`;
        return this.api.patch(this.data.userType === 'resident' ? 'domainBD' : 'oauthUrl', url, data).subscribe(
            (res) => {
                const localData = this.getLocalProfileData();
                localData.email = res.email;
                this.email = res.email;
                this.account.saveUser(JSON.stringify(localData));
                if (this.data.userType !== 'resident') {
                    this.get().subscribe(res => {
                        toastr.success('Profile successfully updated.');
                        this.spinnerShow = false;
                    });
                } else {
                    this.data = res;
                    this.data.avatar = res.photo;
                    toastr.success('Profile successfully updated.');
                    this.spinnerShow = false;
                }
            },
            (error) => {
                try {
                    const body = JSON.parse(error._body);
                    this.parseErrorFields(body.fields);
                } catch (err) {
                    this.api.errorHandler(err, 'Update profile failed!');
                }
                this.spinnerShow = false;
            }
        );
    }

    public updatePassword(data: Object, form) {
        this.disabledSubmit = true;
        const url = `/api/users/change_password.json`;
        return this.api.patch('oauthUrl', url, data).subscribe(
            (res) => {
                form.resetForm();
                this.disabledSubmit = false;
                this.isPasswordFormActive = false;
                toastr.success('Password successfully updated.');
                this.get().subscribe();
            },
            (error) => {
                try {
                    this.disabledSubmit = false;
                    const body = JSON.parse(error._body);
                    this.parseErrorFields(body.fields);
                } catch (err) {
                    this.api.errorHandler(error, 'Update password failed!');
                }
            }
        );
    }

    private parseErrorFields(fields) {
        const errorFields = Object.keys(fields);

        errorFields.forEach(field => {
            if (fields[field].errors) {
                toastr.error(fields[field].errors[0].description);
            } else {
                this.parseErrorFields(fields[field]);
            }
        });
    }

    private getLocalProfileData(): any {
        return JSON.parse(localStorage.getItem('userData'));
    }

    public getResidentProfileData() {
        const residentId = JSON.parse(localStorage.getItem('residentselectedBuilding')).residentBdId;
        const url = `/v1/residents/${residentId}.json?expand=phones,addresses,consent,suiteUser`;
        this.spinnerShow = true;
        this.api.get('domainBD', url).subscribe(
            res => {
                const response = JSON.parse(res._body);
                this.data = response;
                this.data.avatar = response.photo;
                this.getResidentInfo().subscribe(
                    res => {
                        const body = JSON.parse(res._body);
                        this.data.keyWaiverSigned = body.keyWaiverSigned;
                        this.data.emailWaiverSigned = body.emailWaiverSigned;
                        this.data.consent = body.consent;
                        this.data.parcelWaiverSigned = body.parcelWaiverSigned;
                        this.data.suiteUserId = body.suiteUser[0].id;
                        this.data.emergencyContact = body.emergencyContact;
                        this.data.emergencyContacts = body.emergencyContacts || [];
                        this.data.userProfile = {
                            id: body.id,
                            email: body.email,
                            fullName: body.fullName,
                            dateOfBirth: body.dateOfBirth,
                            emergencyContacts: body.emergencyContacts || []
                        };
                        this.data.phones = body.phones.map(p => ({
                            id: p.id,
                            phone: p.phone,
                            isPrimaryPhone: p.isPrimaryPhone,
                            phoneType: p.phoneType
                        }));
                        this.data.addresses = body.addresses.map(a => ({
                            id: a.id,
                            address: a.address,
                            city: a.city,
                            postalCode: a.postalCode,
                            state: a.state,
                            country: a.country,
                            isPrimaryAddress: a.isPrimaryAddress
                        }));
                        // tenants: [{
                        //     fullName: 'tenant name',
                        //     email: 'tenant email',
                        // }]

                        this.getResidentSuiteInfo().subscribe(
                            res => {
                                const body = JSON.parse(res._body);
                                const residentId = JSON.parse(localStorage.getItem('residentselectedBuilding')).residentBdId;
                                this.data.vehicles = body.vehicles.filter(v => v.owner && v.owner.id == residentId)
                                    .map(v => ({
                                        id: v.id,
                                        brand: v.brand,
                                        model: v.model,
                                        licensePlate: v.licensePlate,
                                        color: v.color,
                                        year: v.year,
                                        note: v.note
                                    }));

                                this.data.pets = body.pet.map(p => ({
                                    id: p.id,
                                    name: p.name,
                                    breed: p.breed,
                                    weight: p.weight,
                                    height: p.height,
                                    dateOfBirth: p.dateOfBirth,
                                    petType: p.petType,
                                    note: p.note
                                }));

                                this.getResidentPatchedInfo();
                            }, error => this.spinnerShow = false
                        );
                        this.spinnerShow = false;
                        if (this.data.userType === 'resident')
                            this.getCorporationSettings();
                    }
                );
            },
            error => this.spinnerShow = false
        );
    }

    public getResidentInfo() {
        const residentId = JSON.parse(localStorage.getItem('residentselectedBuilding')).residentBdId;
        const url = `/v1/residents/${residentId}?expand=phones,addresses,groups.group,suiteUser,suiteUser.suite,suiteUser.building,consent`;
        return this.api.get('domainBD', url);
    }

    public getResidentSuiteInfo() {
        const suiteId = this.getResidentSuiteID();
        const url = `/v1/suites/${suiteId}?expand=building,vehicles,vehicles.owner,pet`;
        return this.api.get('domainBD', url);
    }

    private getResidentSuiteID() {
        return JSON.parse(localStorage.getItem('residentselectedBuilding')).suite_id;
    }

    public getResidentInformationPatches() {
        const url = '/v1/resident_patch.json?processedAt=is_null_value';
        return this.api.get('domainBD', url).pipe(
            map(res => {
                const patch = JSON.parse(res._body).entities[0];

                if (patch.userProfile) {
                    patch.userProfile.avatar = patch.userProfile.photo; // todo: fix that naming bug!
                }

                return patch;
            })
        );
    }

    public saveResidentConsentDetails(value, data) {
        if (value) {
            const url = `/v1/consent.json`;
            this.api.post('domainBD', url, data).subscribe(
                res => this.onConsentUpdated.emit({created: true, consent: res}),
                error => this.api.errorHandler(error, 'Failed to save electronic signature consent.')
            );
        } else {
            const url = `/v1/consent/${this.data.consent.id}.json`;
            this.api.remove('domainBD', url).subscribe(
                res => {
                    this.onConsentUpdated.emit({created: false, consent: null});
                    delete this.data.consent;
                },
                error => this.api.errorHandler(error, 'Failed to remove electronic signature consent.')
            );
        }
    }

    updateResidentInfoSection(sectionName) {
        const suiteId = this.getResidentSuiteID();
        const addresses = this.data.addresses.map(a => {
            const ac = {...a};
            delete ac.user;
            return ac;
        });
        const phones = this.data.phones.map(p => {
            const pc = {...p};
            delete pc.user;
            return pc;
        });

        const emergencyContacts = this.data.userProfile.emergencyContacts;
        if (this.data.previousPatch) {
            const data: IResidentPatch & { userProfile: UserAvatar } = JSON.parse(JSON.stringify(this.data.previousPatch));

            if (sectionName == 'userProfile') {
                data[sectionName] = this.data[sectionName];
                data.phones = phones;
                data.addresses = addresses;
                data.userProfile.photo = this.data.avatar;
            } else if (sectionName == 'emergencyContacts') {
                data.userProfile = this.data.userProfile;
                if (data.userProfile && data.userProfile.avatar) {
                    data.userProfile.photo = this.data.avatar;
                }
                data.userProfile.emergencyContacts = emergencyContacts.reduce((contacts, contact) => {
                    contact.suite = this.data.suiteUser[0].suite;
                    contacts.push(contact);
                    return contacts;
                }, []);
            } else if (sectionName == 'vehicles') {
                data[sectionName] = this.data[sectionName].map(v => ({
                    ...v,
                    owner: JSON.parse(localStorage.getItem('residentselectedBuilding')).residentBdId
                }));
            } else {
                data[sectionName] = this.data[sectionName];
            }

            const url = `/v1/resident_patch/${data.id}.json`;
            delete data.id;
            delete data.suite;
            delete data.processedAt;
            delete data.processedBy;
            delete data.createdAt;

            return this.api.patch('domainBD', url, data);
        } else {
            const data: Partial<IResidentPatch> = {
                suite: this.getResidentSuiteID(),
                suiteUser: this.data.suiteUserId as number
            };

            data[sectionName] = this.data[sectionName];

            if (sectionName == 'userProfile') {
                data.phones = phones;
                data.addresses = addresses;
                data.userProfile.photo = this.data.avatar;
            } else if (sectionName == 'vehicles') {
                data[sectionName] = this.data[sectionName].map(v => ({
                    ...v,
                    owner: JSON.parse(localStorage.getItem('residentselectedBuilding')).residentBdId
                }));
            } else if (sectionName == 'emergencyContacts') {
                delete (<any>data).emergencyContacts;
                (<Partial<IUser>>data.userProfile) = {
                    emergencyContacts: this.data.userProfile.emergencyContacts as IEmergencyContacts[]
                };
            }

            if (data.userProfile && data.userProfile.emergencyContacts) {
                data.userProfile.emergencyContacts.forEach(c => {
                    c.suite = suiteId;
                    delete c.user;
                });
            }

            return this.api.post('domainBD', '/v1/resident_patch.json', data);
        }
    }

    getPetTypes(): void {
        if (this.petTypes.length) return;
        const url = '/v1/pet_type.json?per-page=1000&order-by=name|asc';
        this.api.get('domainBD', url).subscribe((res) => {
            const entities = JSON.parse(res._body).entities;
            this.petTypes = entities;
        });
    }

    getResidentPatchedInfo() {
        this.getResidentInformationPatches()
            .filter(el => +el.suite === +this.getResidentSuiteID())
            .subscribe(patch => {
                    this.data.previousPatch = patch;

                    if (patch) {
                        if (patch.userProfile) {
                            if (patch.userProfile.avatar)
                                this.data.avatar = patch.userProfile.avatar;
                            if (patch.userProfile.dateOfBirth)
                                this.data.userProfile.dateOfBirth = patch.userProfile.dateOfBirth;
                            if (patch.userProfile.email)
                                this.data.userProfile.email = patch.userProfile.email;
                            if (patch.userProfile.emergencyContacts)
                                this.data.userProfile.emergencyContacts = patch.userProfile.emergencyContacts;
                            if (patch.userProfile.fullName)
                                this.data.userProfile.fullName = patch.userProfile.fullName;
                        }
                        if (patch.addresses)
                            this.data.addresses = patch.addresses;

                        if (patch.phones)
                            this.data.phones = patch.phones;

                        if (patch.vehicles && patch.vehicles.length) {
                            patch.vehicles.forEach(vehicle => {
                                if (this.data.vehicles && this.data.vehicles.length > 0) {
                                    const index = this.data.vehicles.findIndex(v => v.id == vehicle.id);
                                    if (vehicle.id && index != -1)
                                        this.data.vehicles[index] = vehicle;
                                    else
                                        this.data.vehicles.push(vehicle);
                                } else {
                                    if (!this.data.vehicles)
                                        this.data.vehicles = [];
                                    this.data.vehicles.push(vehicle);
                                }
                            });
                        }
                        if (patch.pets && patch.pets.length) {
                            patch.pets.forEach(pet => {
                                if (this.data.pets && this.data.pets.length > 0) {
                                    const index = this.data.pets.findIndex(p => p.id == pet.id);
                                    if (pet.id && index != -1)
                                        this.data.pets[index] = pet;
                                    else
                                        this.data.pets.push(pet);
                                } else {
                                    if (!this.data.pets)
                                        this.data.pets = [];
                                    this.data.pets.push(pet);
                                }
                            });
                        }
                    }
                }, error => this.spinnerShow = false
            );
    }

    getCorporationSettings() {
        const corporationSettings = JSON.parse(localStorage.getItem('residentselectedBuilding')).cNSettings;
        this.currentCorporationSettings = corporationSettings ? corporationSettings : {};
    }
}



// WEBPACK FOOTER //
// /app/src/app/views/profile/services/profile.service.ts